import { useState, useEffect } from "react";
import { toast } from "sonner";
import { KeyRound, Shuffle, Copy, Check, Mail, Monitor } from "lucide-react";
import { fetchSecurityInfo, changeGatePassword, suggestGatePassword, setLoginAlerts, formatApiError } from "../../lib/api";

const Field = ({ label, value, onChange, type = "password", testid, autoComplete = "new-password" }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)] block mb-2">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      autoComplete={autoComplete}
      className="w-full bg-transparent border border-[var(--vsl-border)] px-3 py-2 font-mono text-sm outline-none focus:border-[var(--vsl-fg)] transition-colors" />
  </label>
);

const deviceLabel = (ua = "") => {
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "Unknown browser";
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android"
    : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows"
    : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} · ${os}` : browser;
};

export default function SecurityPanel() {
  const [info, setInfo] = useState(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchSecurityInfo().then(setInfo).catch((e) => toast.error(formatApiError(e))); }, []);

  const generate = async () => {
    try {
      const { password } = await suggestGatePassword();
      setNext(password);
      setConfirm(password);
      await navigator.clipboard.writeText(password).catch((err) => console.warn("clipboard blocked:", err));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Password generated and copied — save it somewhere safe");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyNew = async () => {
    try {
      await navigator.clipboard.writeText(next);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) { toast.error("Could not copy — select the text manually"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("The two new passwords do not match");
    if (next.length < 12) return toast.error("New password must be at least 12 characters");
    setSaving(true);
    try {
      const res = await changeGatePassword(current, next);
      setInfo((p) => ({ ...(p || {}), password_updated_at: res.updated_at }));
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Password changed — use it next time you sign in");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const toggleAlerts = async (enabled) => {
    setInfo((p) => ({ ...(p || {}), login_alerts: enabled }));
    try {
      await setLoginAlerts(enabled);
      toast.success(enabled ? "Sign-in alerts on" : "Sign-in alerts off");
    } catch (e) {
      setInfo((p) => ({ ...(p || {}), login_alerts: !enabled }));
      toast.error(formatApiError(e));
    }
  };

  const updated = info?.password_updated_at
    ? new Date(info.password_updated_at).toLocaleString()
    : "never changed in the CMS";

  return (
    <div data-testid="security-panel" className="max-w-[640px] space-y-10">
      <section className="space-y-3">
        <h3 className="font-display text-3xl">Back office access</h3>
        <div className="border border-[var(--vsl-border)] p-5 space-y-2">
          <p className="font-mono text-[11px] text-[var(--vsl-muted)]">
            Username <span className="text-[var(--vsl-fg)]" data-testid="security-username">{info?.username || "—"}</span>
          </p>
          <p className="font-mono text-[11px] text-[var(--vsl-muted)]">
            Password last changed <span className="text-[var(--vsl-fg)]" data-testid="security-updated">{updated}</span>
          </p>
          <p className="font-mono text-[11px] text-[var(--vsl-muted)] flex items-center gap-2">
            <Mail size={12} /> Sign-in alerts and login codes go to
            <span className="text-[var(--vsl-fg)]" data-testid="security-email">{info?.alert_email || "—"}</span>
          </p>
          <div className="flex items-center justify-between pt-3 border-t border-[var(--vsl-border)] mt-3">
            <span className="font-mono text-[11px] text-[var(--vsl-muted)]">Email me on every sign-in</span>
            <button type="button" onClick={() => toggleAlerts(!info?.login_alerts)} data-testid="toggle-alerts"
              aria-pressed={!!info?.login_alerts}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${info?.login_alerts ? "bg-[var(--vsl-fg)]" : "bg-[#2a2a28]"}`}>
              <span className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-[var(--vsl-bg)] transition-all ${info?.login_alerts ? "left-[26px]" : "left-[3px]"}`} />
            </button>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-3xl">Recent sign-ins</h3>
        {info?.sessions?.length ? (
          <ul className="border border-[var(--vsl-border)] divide-y divide-[var(--vsl-border)]" data-testid="sessions-list">
            {info.sessions.map((s) => (
              <li key={s.id} data-testid="session-row" className="px-4 py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-mono text-[11px] text-[var(--vsl-fg)] flex items-center gap-2">
                    <Monitor size={12} className="shrink-0" /> {deviceLabel(s.user_agent)}
                  </p>
                  <p className="font-mono text-[10px] text-[var(--vsl-muted)] mt-1 truncate">IP {s.ip}</p>
                </div>
                <span className="font-mono text-[10px] text-[var(--vsl-muted)] whitespace-nowrap">
                  {new Date(s.created_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="font-mono text-[11px] text-[var(--vsl-muted)]" data-testid="sessions-empty">No sign-ins recorded yet.</p>
        )}
      </section>

      <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
        <h3 className="font-display text-3xl">Change password</h3>
        <p className="font-mono text-[11px] text-[var(--vsl-muted)]">
          This is the password for step one of the back office sign-in. Minimum 12 characters.
          The emailed code and your username stay the same.
        </p>
        <Field label="Current password" value={current} onChange={setCurrent} testid="current-password" autoComplete="current-password" />
        <Field label="New password" value={next} onChange={setNext} testid="new-password" />
        <Field label="Repeat new password" value={confirm} onChange={setConfirm} testid="confirm-password" />
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={generate} data-testid="generate-password"
            className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-2 hover:border-[var(--vsl-fg)] transition-colors">
            <Shuffle size={13} /> Generate strong password
          </button>
          {next && (
            <button type="button" onClick={copyNew} data-testid="copy-password"
              className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 border border-[var(--vsl-border)] px-3 py-2 hover:border-[var(--vsl-fg)] transition-colors">
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
            </button>
          )}
        </div>
        <button type="submit" disabled={saving || !current || !next} data-testid="save-password"
          className="font-mono text-[11px] tracking-[0.2em] uppercase inline-flex items-center gap-2 bg-[var(--vsl-fg)] text-[var(--vsl-bg)] px-5 py-3 hover:bg-[var(--vsl-limestone)] transition-colors disabled:opacity-40">
          <KeyRound size={14} /> {saving ? "Saving…" : "Change password"}
        </button>
      </form>
    </div>
  );
}
